import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarcodeRead } from '@fortawesome/pro-regular-svg-icons/faBarcodeRead';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { INVENTORY_UOM_SCAN_MATCHED_BY_UOM } from '../../../constants/analytics-events';
import { unspecifiedWarehouseLocation } from '../../../constants/unspecified-warehouse-location';
import { useBinaryState } from '../../../hooks/use-binary-state';
import { useSendMetrics } from '../../../hooks/use-send-metrics';
import Global from '../../../store/global-selectors';
import { WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { getProductsBySkuOrUpc } from '../scan-to-recieve.api';
import { EACH_UOM } from '../../../constants/units-of-measure';

import { messages } from './messages';
import styles from './styles.module.less';
import { useScanSound } from './use-scan-sound';

/**
 * @typedef {import('../../../prop-types/intl-types').MessageDescriptor} MessageDescriptor
 */

/**
 * @param {object} props
 * @param {string} props.warehouseId
 * @param {boolean} props.disabled
 * @param {(scannedProduct: import('../types').ScannedProduct) => void} props.onSuccessfulScan
 * @param {() => void} props.onFailedScan
 * @param {(skuOrUpc: string) => Promise<import('../scan-to-recieve.api').ScannedProductsResponse>} [props.getProductMethod]
 * @param {(step: string, params?: object) => void} props.trackWorkflow
 * @param {boolean} [props.allowBundles]
 * @param {import('react-intl').MessageDescriptor | null} [props.externalWarning]
 * @param {boolean} [props.includeUom]
 * @returns {React.JSX.Element}
 */
export const ScanProduct = ({
  warehouseId,
  disabled,
  onSuccessfulScan,
  onFailedScan,
  getProductMethod,
  trackWorkflow,
  allowBundles,
  externalWarning = null,
  includeUom = false,
}) => {
  const [scanInput, setScanInput] = useState('');
  const [errorMessage, setErrorMessage] = useState(/** @type {MessageDescriptor | null} */ (null));
  const [isScanProcessing, setStartScanProcessing, setStopScanProcessing] = useBinaryState();

  const warehouseLocations = useSelector((state) =>
    Global.selectSellerInventoryWarehouseLocationsByWarehouseId(state, {
      inventoryWarehouseId: warehouseId,
    }),
  );

  /** @type {React.RefObject<HTMLInputElement>} */
  const scanInputRef = useRef(null);

  const play = useScanSound();
  const trackMetrics = useSendMetrics();

  const handleAddProduct = useCallback(async () => {
    if (!scanInput) {
      return;
    }

    const trimmedInput = scanInput.trim();

    try {
      setStartScanProcessing();
      const response = await (getProductMethod ? getProductMethod(scanInput) : getProductsBySkuOrUpc(scanInput, warehouseId, includeUom));

      // Temporary solution - we can't add bundle right now to the table
      // TODO: https://auctane.atlassian.net/browse/SPD-20317
      if (response.product.isBundle && !allowBundles) {
        setErrorMessage(messages.bundleNotAllowed);
        play('error');
        onFailedScan();
        scanInputRef?.current?.select();
        trackWorkflow(WorkflowSteps.IS_BUNDLE);
        return;
      }

      const selectedProduct = response.product;

      const matchedUom =
        !selectedProduct.isBundle && selectedProduct.matchedUoMId
          ? selectedProduct.unitsOfMeasure?.find((u) => u.productUoMId === selectedProduct.matchedUoMId)
          : undefined;

      if (matchedUom) {
        trackMetrics(INVENTORY_UOM_SCAN_MATCHED_BY_UOM, {
          productId: selectedProduct.productId,
          matchedUoMId: selectedProduct.matchedUoMId,
          uomTypeName: matchedUom.uomTypeName ?? null,
          matchType: matchedUom.barcode === trimmedInput ? 'uom_barcode' : 'uom_sku',
        });
      }

      const unspecifiedLocationId = warehouseLocations.find((wl) => wl?.area === unspecifiedWarehouseLocation)?.inventoryLocationId;
      const initialLocation = selectedProduct.locationId ?? unspecifiedLocationId;

      const mappedUoms = selectedProduct.unitsOfMeasure?.map((u) => ({
        productUnitOfMeasureId: u.productUoMId,
        uomTypeName: u.uomTypeName,
        conversionFactor: u.conversionFactor,
        parentProductUnitOfMeasureId: u.parentProductUoMId ?? null,
        eaches: u.eaches,
      }));
      const unitsOfMeasure = mappedUoms?.length ? [EACH_UOM, ...mappedUoms] : undefined;

      const mapped = {
        productId: selectedProduct.productId,
        name: selectedProduct.name,
        sku: selectedProduct.sku,
        thumbnailUrl: selectedProduct.thumbnailUrl,
        upc: selectedProduct.upc,
        locationId: initialLocation,
        lotId: '',
        expirationDate: '',
        quantity: 1,
        cost: 0,
        isBundle: selectedProduct.isBundle,
        uomId: matchedUom?.productUoMId ?? (unitsOfMeasure ? EACH_UOM.productUnitOfMeasureId : undefined),
        uomTypeName: matchedUom?.uomTypeName ?? (unitsOfMeasure ? EACH_UOM.uomTypeName : undefined),
        conversionFactor: matchedUom?.conversionFactor ?? (unitsOfMeasure ? EACH_UOM.conversionFactor : undefined),
        eaches: matchedUom?.eaches ?? (unitsOfMeasure ? EACH_UOM.eaches : undefined),
        unitsOfMeasure,
        components:
          selectedProduct.bundleComponents?.map((component) => ({
            productId: component.componentProductId,
            upc: component.upc,
            sku: component.sku,
            name: component.name,
            quantity: component.quantity,
            thumbnailUrl: component.thumbnailUrl ?? '',
            cost: 0,
            expirationDate: '',
            lotId: '',
            locationId: initialLocation,
          })) || [],
      };
      onSuccessfulScan(mapped);
      setErrorMessage(null);
      setScanInput('');
      play('success');
    } catch {
      onFailedScan();
      setErrorMessage(messages.noSkuOrUpcError);
      trackWorkflow(WorkflowSteps.NOT_FOUND, { scanInput });
      play('error');
      scanInputRef?.current?.select();
    } finally {
      setStopScanProcessing();
    }
  }, [
    allowBundles,
    getProductMethod,
    includeUom,
    onFailedScan,
    onSuccessfulScan,
    play,
    scanInput,
    setStartScanProcessing,
    setStopScanProcessing,
    trackMetrics,
    trackWorkflow,
    warehouseId,
    warehouseLocations,
  ]);

  const onEnter = useCallback(
    (e) => {
      if (e.key === 'Enter' && !isScanProcessing) {
        handleAddProduct();
      }
    },
    [handleAddProduct, isScanProcessing],
  );

  const onChangeScanInput = useCallback((e) => {
    setErrorMessage(null);
    setScanInput(e.target.value);
  }, []);

  const scanInputInlineMessage = useMemo(() => {
    if (errorMessage) {
      return (
        <Text
          className={styles['scan-error']}
          formattedMessageValues={{
            value: scanInput,
          }}
          textType={TextType.Caption}
          value={errorMessage}
        />
      );
    }
    if (externalWarning) {
      return <Text className={styles['scan-warning']} textType={TextType.Caption} value={externalWarning} />;
    }
  }, [errorMessage, externalWarning, scanInput]);

  const inlineMessageType = useMemo(() => {
    if (errorMessage) return INLINE_MESSAGE_TYPES.error;
    if (externalWarning) return INLINE_MESSAGE_TYPES.warning;
    return undefined;
  }, [errorMessage, externalWarning]);

  const { formatMessage } = useIntl();

  useEffect(() => {
    if (!isScanProcessing && !disabled && warehouseId) {
      scanInputRef?.current?.focus();
    }
  }, [isScanProcessing, disabled, warehouseId]);

  return (
    <div className={styles['form-group-scan']}>
      <div>
        <div className={styles['input-wrapper']}>
          <Input
            ref={scanInputRef}
            className={styles['scan-input']}
            disabled={disabled || isScanProcessing}
            inlineMessage={scanInputInlineMessage}
            inlineMessageType={inlineMessageType}
            onChange={onChangeScanInput}
            onKeyDown={onEnter}
            placeholder={formatMessage(messages.scanUpcSku)}
            value={scanInput}
            version={version2}
          />
          <FontAwesomeIcon className={styles['autocomplete-scanner-icon']} icon={faBarcodeRead} />
        </div>
        <Text className={styles['helper-text']} textType={TextType.Caption} value={messages.workWithScanner} />
      </div>
      <Button
        className={styles['enter-button']}
        disabled={isScanProcessing || !scanInput || disabled}
        onClick={handleAddProduct}
        theme={THEME_CONSTANTS.tertiary}
      >
        <FormattedMessage {...messages.add} />
      </Button>
    </div>
  );
};
